import { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useActiveRole } from "@/hooks/useActiveRole";

interface AssignedPatient {
  id: string;
  full_name: string;
}

interface ActivePatientContextType {
  /** The id of the patient the carer is currently viewing/editing. */
  activePatientId: string | null;
  /** All patients this carer has accepted access to (empty for non-carers). */
  assignedPatients: AssignedPatient[];
  /** Set the active patient (persisted per-user in localStorage). */
  setActivePatient: (patientId: string) => void;
  loading: boolean;
}

const ActivePatientContext = createContext<ActivePatientContextType>({
  activePatientId: null,
  assignedPatients: [],
  setActivePatient: () => {},
  loading: true,
});

const storageKey = (userId: string) => `medcarer_active_patient:${userId}`;

export function ActivePatientProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { activeRole } = useActiveRole();
  const [assignedPatients, setAssignedPatients] = useState<AssignedPatient[]>([]);
  const [activePatientId, setActivePatientIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || activeRole !== "carer") {
      setAssignedPatients([]);
      setActivePatientIdState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("carer_assignments")
        .select("patient_id, patients(id, full_name)")
        .eq("carer_user_id", user.id)
        .eq("status", "accepted");
      const list: AssignedPatient[] = (data || [])
        .map((r: any) => r.patients)
        .filter(Boolean);
      setAssignedPatients(list);

      const stored = localStorage.getItem(storageKey(user.id));
      const validStored = stored && list.some((p) => p.id === stored) ? stored : null;
      setActivePatientIdState(validStored ?? list[0]?.id ?? null);
      setLoading(false);
    })();
  }, [user, activeRole]);

  const setActivePatient = useCallback(
    (patientId: string) => {
      setActivePatientIdState(patientId);
      if (user) localStorage.setItem(storageKey(user.id), patientId);
    },
    [user],
  );

  const value = useMemo(
    () => ({ activePatientId, assignedPatients, setActivePatient, loading }),
    [activePatientId, assignedPatients, setActivePatient, loading],
  );

  return <ActivePatientContext.Provider value={value}>{children}</ActivePatientContext.Provider>;
}

export const useActivePatient = () => useContext(ActivePatientContext);
